/**
 * 景點的「智慧窗屏內容」欄位組（純文字多語系）。
 *
 * 從 SpotEdit 抽出來的原因有二：一是該頁加上這六個欄位後會超過 500 行；二是這組
 * 欄位的語意獨立——它們不是給 PWA / kiosk 看的行銷文案，而是會被 Phase 2 產生器
 * 逐字寫進船上檔案的內容，欄位旁標註產出檔名可以讓編輯人員知道自己在填什麼。
 *
 * 刻意用純文字 textarea 而非 RichTextEditor：窗屏讀 `.txt`，HTML 標籤會原樣顯示。
 */

import { FIELD_MAX_WIDTH } from './spot-form';
import type { SpotDisplayContent } from '../lib/api-client';

/** CMS 語系代碼；窗屏檔案樹的 zh/en/jp 由後端產生器映射。 */
export const DISPLAY_LOCALES = ['zh-TW', 'en', 'ja'] as const;
export type DisplayLocale = (typeof DISPLAY_LOCALES)[number];

export type DisplayField =
  | 'location_name'
  | 'poi_title'
  | 'poi_desc'
  | 'intro'
  | 'detail_title'
  | 'detail_text';

export type DisplayFormState = Record<DisplayField, Record<DisplayLocale, string>>;

interface FieldSpec {
  key: DisplayField;
  label: string;
  /** 這個欄位最終會寫進哪些檔案——編輯者看得到才知道填錯會壞在哪 */
  outputs: string;
  hint?: string;
  multiline: boolean;
}

export const DISPLAY_FIELDS: readonly FieldSpec[] = [
  {
    key: 'location_name',
    label: '地標名稱 locationName',
    outputs: 'roiN.txt',
    hint: '窗屏一階畫面上方的地標文字',
    multiline: false,
  },
  {
    key: 'poi_title',
    label: '一階標題 poiTitle',
    outputs: 'roiN.txt · 1_str1.txt',
    multiline: false,
  },
  {
    key: 'poi_desc',
    label: '一階描述 poiDesc',
    outputs: 'roiN.txt · 1_str2.txt',
    multiline: true,
  },
  {
    key: 'intro',
    label: '景點介紹',
    outputs: '1_str3.txt',
    multiline: true,
  },
  {
    key: 'detail_title',
    label: '二階標題 detailTitle',
    outputs: 'roiN.txt · 2_str1.txt',
    multiline: false,
  },
  {
    key: 'detail_text',
    label: '二階詳細介紹 detailText',
    outputs: 'roiN.txt · 2_str2.txt',
    multiline: true,
  },
];

export function emptyDisplayForm(): DisplayFormState {
  const blank = (): Record<DisplayLocale, string> => ({ 'zh-TW': '', en: '', ja: '' });
  return {
    location_name: blank(),
    poi_title: blank(),
    poi_desc: blank(),
    intro: blank(),
    detail_title: blank(),
    detail_text: blank(),
  };
}

/** Spot API 回應 → 表單狀態。 */
export function displayFormFromSpot(spot: SpotDisplayContent): DisplayFormState {
  const pick = (rec: Record<string, string> | null | undefined): Record<DisplayLocale, string> => ({
    'zh-TW': rec?.['zh-TW'] ?? '',
    en: rec?.en ?? '',
    ja: rec?.ja ?? '',
  });
  return {
    location_name: pick(spot.location_name_i18n),
    poi_title: pick(spot.poi_title_i18n),
    poi_desc: pick(spot.poi_desc_i18n),
    intro: pick(spot.intro_i18n),
    detail_title: pick(spot.detail_title_i18n),
    detail_text: pick(spot.detail_text_i18n),
  };
}

/**
 * 表單狀態 → API payload。
 *
 * 全空的欄位送 `{}` 而不是省略：省略代表「不變更」，使用者清空內容時會改不掉。
 */
export function displayFormToDto(form: DisplayFormState): SpotDisplayContent {
  const clean = (rec: Record<DisplayLocale, string>): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const l of DISPLAY_LOCALES) {
      const v = rec[l].trim();
      if (v) out[l] = v;
    }
    return out;
  };
  return {
    location_name_i18n: clean(form.location_name),
    poi_title_i18n: clean(form.poi_title),
    poi_desc_i18n: clean(form.poi_desc),
    intro_i18n: clean(form.intro),
    detail_title_i18n: clean(form.detail_title),
    detail_text_i18n: clean(form.detail_text),
  };
}

/** 該語系是否已有任何窗屏內容（給語言頁籤圓點用）。 */
export function displayLocaleHasContent(form: DisplayFormState, l: DisplayLocale): boolean {
  return DISPLAY_FIELDS.some((f) => form[f.key][l].trim().length > 0);
}

/** 已填欄位數 / 總欄位數（該語系）——完整度提示。 */
export function displayFilledCount(form: DisplayFormState, l: DisplayLocale): [number, number] {
  const filled = DISPLAY_FIELDS.filter((f) => form[f.key][l].trim().length > 0).length;
  return [filled, DISPLAY_FIELDS.length];
}

interface Props {
  value: DisplayFormState;
  onChange: (next: DisplayFormState) => void;
  locale: DisplayLocale;
  localeLabel: string;
  disabled?: boolean;
  /** 區塊標題；分頁後同一組欄位會拆成「一階畫面」「二階畫面」兩塊。 */
  title?: string;
  /** 只顯示這些欄位；省略則全部。順序仍以 DISPLAY_FIELDS 為準。 */
  only?: readonly DisplayField[];
}

export function SpotDisplayFields({
  value,
  onChange,
  locale,
  localeLabel,
  disabled,
  title,
  only,
}: Props): JSX.Element {
  const setField = (key: DisplayField, text: string): void => {
    onChange({ ...value, [key]: { ...value[key], [locale]: text } });
  };

  const shown = only ? DISPLAY_FIELDS.filter((f) => only.includes(f.key)) : DISPLAY_FIELDS;
  // 完整度只數這一塊的欄位——分頁後顯示全域的 4/6 會讓人在「二階畫面」上找不到
  // 那兩個沒填的欄位
  const filled = shown.filter((f) => value[f.key][locale].trim().length > 0).length;
  const total = shown.length;

  return (
    <fieldset
      style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, margin: '12px 0' }}
    >
      <legend style={{ fontSize: 13, fontWeight: 600 }}>
        {title ?? '智慧窗屏內容'}（{localeLabel}）
        <span
          style={{
            marginLeft: 8,
            fontWeight: 400,
            color: filled === total ? '#10b981' : '#9ca3af',
          }}
        >
          {filled}/{total} 已填
        </span>
      </legend>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
        這些內容會被逐字寫進船上窗屏的文字檔，請填<strong>純文字</strong>（不支援排版標籤）。
        「基本資料」的描述欄位是給網站與購票機用的，不會送到窗屏。
      </p>

      {shown.map((f) => (
        <div key={f.key} style={{ marginBottom: 10, maxWidth: FIELD_MAX_WIDTH }}>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 3 }}>
            {f.label}
            <span
              style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}
            >
              → {f.outputs}
            </span>
          </label>
          {f.multiline ? (
            <textarea
              value={value[f.key][locale]}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={disabled ?? false}
              rows={3}
              style={{ width: '100%', padding: 8, fontFamily: 'inherit', resize: 'vertical' }}
            />
          ) : (
            <input
              type="text"
              value={value[f.key][locale]}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={disabled ?? false}
              style={{ width: '100%', padding: 8 }}
            />
          )}
          {f.hint && <small style={{ color: '#9ca3af' }}>{f.hint}</small>}
        </div>
      ))}
    </fieldset>
  );
}
